import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { API } from 'aws-amplify';
import { Observable, from } from 'rxjs';
import { Phone } from './core.module';
import { DEFAULT_GET_LIMIT } from '../shared/constants/operationals';

export const BUYERS_API_NAME: string = 'buyers';
export const BUYERS_API_ENDPOINT: string = environment.API_BASE_PATH + '/buyer';

@Injectable({
  providedIn: 'root',
})
export class BuyersService {
  constructor() {}

  listBuyers(list_params?: ListBuyersParams): Observable<any> {
    const api_name: string = BUYERS_API_NAME;
    const path: string = '';

    let query_string_params: any = {
      cursor: list_params?.cursor || null,
      limit: list_params?.limit || DEFAULT_GET_LIMIT,
    };
    if (list_params?.buyer_id) query_string_params['buyer_id'] = list_params.buyer_id;
    if (list_params?.buyer_name) query_string_params['buyer_name'] = list_params.buyer_name;
    if (list_params?.phone_value) query_string_params['phone_value'] = list_params.phone_value;
    if (list_params?.email) query_string_params['email'] = list_params.email;
    if (list_params?.onboarding_completed_from)
      query_string_params['onboarding_completed_from'] = list_params.onboarding_completed_from;
    if (list_params?.onboarding_completed_to)
      query_string_params['onboarding_completed_to'] = list_params.onboarding_completed_to;

    const init = { response: true, queryStringParameters: query_string_params };
    console.log('listBuyers - queryStringParameters:', query_string_params);
    return from(API.get(api_name, path, init));
  }

  readSummary(): Observable<any> {
    const api_name: string = BUYERS_API_NAME;
    const path: string = '/summary';
    const init = { response: true };
    return from(API.get(api_name, path, init));
  }

  edit(buyer: Buyer): Observable<any> {
    const api_name: string = BUYERS_API_NAME;
    const path: string = `/edit`;
    const init = {
      response: true,
      body: buyer,
    };
    return from(API.put(api_name, path, init));
  }

  unblock(buyer: Buyer): Observable<any> {
    const api_name: string = BUYERS_API_NAME;
    const path: string = `/${buyer.buyer_id}/unblock`;
    const init = { response: true, body: buyer };
    return from(API.post(api_name, path, init));
  }

  setBlockByAdmin(buyer_id: string, block: boolean): Observable<any> {
    const api_name: string = BUYERS_API_NAME;
    const path: string = `/set-block-by-admin`;
    const init = {
      response: true,
      body: {
        buyer_id,
        block,
      },
    };
    return from(API.put(api_name, path, init));
  }

  confirmByAdmin(buyer_id: string, confirm_reason: string): Observable<any> {
    const api_name: string = BUYERS_API_NAME;
    const path: string = `/${buyer_id}/confirm-otp`;
    const init = {
      response: true,
      body: { confirm_reason },
    };
    return from(API.post(api_name, path, init));
  }

  readBuyer(buyer_id: string): Observable<any> {
    const api_name: string = BUYERS_API_NAME;
    const path: string = `/${buyer_id}`;
    const init = { response: true };
    return from(API.get(api_name, path, init));
  }

  readBuyerDocument(buyer_id: string): Observable<any> {
    const api_name: string = BUYERS_API_NAME;
    const path: string = `/${buyer_id}/read-document-url`;
    const init = { response: true };
    return from(API.get(api_name, path, init));
  }

  listOrders(buyer_id: string): Observable<any> {
    const api_name: string = BUYERS_API_NAME;
    const path: string = `/${buyer_id}/list-order`;
    const init = { response: true };
    return from(API.get(api_name, path, init));
  }

  getWhatsappLink(buyer_id: string): Observable<any> {
    const api_name: string = BUYERS_API_NAME;
    const path: string = `/${buyer_id}/whatsapp-url`;
    const init = { response: true };
    return from(API.get(api_name, path, init));
  }
}

export interface ListBuyersParams {
  buyer_id?: string;
  buyer_name?: string;
  phone_value?: string;
  email?: string;
  onboarding_completed_from?: string;
  onboarding_completed_to?: string;

  cursor?: string | null;
  limit?: number;
}

export interface Buyer {
  buyer_id?: string;
  object?: 'buyer';
  buyer_sub?: string;
  cdg?: string;
  is_confirmed?: boolean;
  confirmed_by?: string;
  confirmed_by_ext_id?: string;
  confirm_reason?: string;
  is_always_reliable?: boolean;
  is_never_reliable?: boolean;
  firstname?: string;
  lastname?: string;
  phone?: Phone;
  email?: string;
  tax_code?: string;
  birthday?: number;
  birthregion?: string;
  birthcountry?: string;
  birthregion_code?: string;
  birthplace?: string;
  gender?: string;
  img_url?: string;
  origin?: string;
  origin_store_id?: string;
  origin_store_name?: string;
  origin_company_id?: string;
  origin_company_name?: string;
  aml?: AML;
  on_boarding_steps?: OnboardingSetps;
  communication_channel?: CommChannel;
  terms?: BuyerTerms;
  document?: BuyerDocument;
  residency?: BuyerResidency;
  domicile?: BuyerDomicile;
  citizenship?: string;
  secondary_citizenship?: string;
  latest_edit?: BuyerLatestEdit;
  credit_constraints?: CreditConstraints;
  payment_constraints?: PaymentMethodConstraints;
  step_constraints?: PlanConstraints;
  created_at?: number;
  updated_at?: number;
  blocked_at?: number;
  blocked_reason?: string;
  blocked_by_admin?: boolean;
  current_login_attempts?: number;
  last_login_at?: number;
  stripe_customer_id?: string;
  last_experian_verification_id?: string;
  last_fourthline_verification_id?: string;
  last_fea_contract_id?: string;
  origin_order_id?: string;
}

export interface BuyerDocument {
  buyer_id?: string;
  buyer_doc_id?: string;
  buyer_doc_path?: string;
}

export enum BuyerOrigin {
  in_store = 'in_store',
  in_order = 'in_order',
  website = 'website',
}

export interface AML {
  activity?: string;
  sector?: string;
  occupation?: string;
  business_place?: string;
  is_apical_subject?: boolean;
  tae?: string;
  activity_code?: string;
  sector_code?: string;
  occupation_code?: string;
}

export interface OnboardingSetps {
  phone_verified_at?: number;
  payment_source_set_at?: number;
  experian_verification_succeeded_at?: number;
  fourthline_verification_succeeded_at?: number;
  aml_set_at?: number;
  consensus_terms_set_at?: number;
  communication_channel_set_at?: number;
  fea_contract_signed_at?: number;
  contract_signed_at?: number;
}

export interface BuyerTerms {
  is_marketing_terms_accepted?: boolean;
  is_third_parts_terms_accepted?: boolean;
  is_analysis_terms_accepted?: boolean;
}

export interface CreditConstraints {
  min_amount?: number;
  max_amount?: number;
}

export interface PaymentMethodConstraints {
  is_card_enabled?: boolean;
  is_sepa_enabled?: boolean;
  is_bank_transfer_enabled?: boolean;
}

export interface PlanConstraints {
  max_plan_length?: number;
  min_plan_length?: number;
  max_plan_duration?: number;
}

export enum TermsConsensus {
  accept = 'accept',
  deny = 'deny',
}

export enum CommChannel {
  digital = 'digital',
  paper = 'paper',
}

export interface BuyerSummary {
  length: number;
  onboarding_completed: number;
  confirmed: number;
  not_confirmed: number;
}

export interface BuyerDocument {
  issuer: string;
  doc_type: string;
  doc_number: string;
  issuer_type: string;
  issue_province: string;
  issue_country: string;
  emission_date: string;
  expiration_date: string;
}

export interface BuyerResidency {
  city: string;
  address: string;
  country: string;
  province: string;
  zip_code: string;
  house_number: string;
}

export interface BuyerDomicile {
  city: string;
  address: string;
  country: string;
  province: string;
  zip_code: string;
  house_number: string;
}

export interface BuyerLatestEdit {
  admin_email: string;
  note: string;
  timestamp: number;
}

export enum ExcludeGdpr {
  yes = 'yes',
  no = 'no',
}
